import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { Activity, DollarSign, LogOut, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth, useLogout } from '@/hooks/useAuth';
import { OptimizationQueueIndicator } from '@/components/optimizations/OptimizationQueueIndicator';
import { OptimizationQueueRunner } from '@/components/optimizations/OptimizationQueueRunner';

const navItems = [
  { to: '/executions', label: 'Executions', icon: Activity },
  { to: '/revenue', label: 'Revenue', icon: DollarSign },
];

export function Layout() {
  const { user } = useAuth();
  const logout = useLogout();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-adv-sunken">
      <header className="bg-adv-card border-b border-adv-border shadow-adv-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4" ref={menuRef}>
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
              <h1 className="text-adv-body-l font-bold text-adv-primary hidden sm:block">
                Campaign Monitoring Panel
              </h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <nav className="flex items-center gap-1">
                {navItems.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      cn(
                        'adv-nav-link px-4',
                        isActive && 'adv-nav-link--active'
                      )
                    }
                  >
                    <Icon size={18} />
                    {label}
                  </NavLink>
                ))}
              </nav>
              <div className="flex items-center gap-3 pl-4 border-l border-adv-border">
                {user && (
                  <div className="flex items-center gap-2">
                    {user.image ? (
                      <img
                        src={user.image}
                        alt={user.name || user.email}
                        className="w-8 h-8 rounded-full"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-adv-gold-crema flex items-center justify-center text-adv-primary font-bold text-adv-body-s">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-adv-body-s text-adv-secondary">
                      {user.name || user.email}
                    </span>
                  </div>
                )}
                <button
                  onClick={logout}
                  className="adv-btn adv-btn--ghost adv-btn--icon"
                  title="Logout"
                >
                  <LogOut size={18} />
                </button>
              </div>
            </div>

            {/* Mobile: User Avatar and Hamburger */}
            <div className="flex md:hidden items-center gap-2">
              {user && (
                <>
                  {user.image ? (
                    <img
                      src={user.image}
                      alt={user.name || user.email}
                      className="w-8 h-8 rounded-full"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-adv-gold-crema flex items-center justify-center text-adv-primary font-bold text-adv-body-s">
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </div>
                  )}
                </>
              )}
              <button
                className="adv-btn adv-btn--ghost adv-btn--icon h-11 w-11"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-adv-border py-4 transition-all">
              <nav className="flex flex-col space-y-1">
                {navItems.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={handleNavClick}
                    className={({ isActive }) =>
                      cn(
                        'adv-nav-link flex gap-3 px-4 py-3 rounded-adv-md',
                        isActive && 'adv-nav-link--active'
                      )
                    }
                  >
                    <Icon size={20} />
                    {label}
                  </NavLink>
                ))}
              </nav>

              {/* Mobile User Section */}
              {user && (
                <div className="mt-4 pt-4 border-t border-adv-border">
                  <div className="flex items-center gap-3 px-4 py-2">
                    {user.image ? (
                      <img
                        src={user.image}
                        alt={user.name || user.email}
                        className="w-10 h-10 rounded-full"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-adv-gold-crema flex items-center justify-center text-adv-primary font-bold">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-adv-body-m font-bold text-adv-primary truncate">
                        {user.name || user.email}
                      </p>
                      {user.name && (
                        <p className="text-adv-body-s text-adv-secondary truncate">
                          {user.email}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      handleNavClick();
                      logout();
                    }}
                    className="adv-nav-link flex w-full gap-3 px-4 py-3 mt-2 rounded-adv-md"
                  >
                    <LogOut size={20} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Outlet />
      </main>

      {/* Mounted here, outside the routes, so queued optimizations keep running across pages. */}
      <OptimizationQueueRunner />
      <OptimizationQueueIndicator />
    </div>
  );
}
